import React, { useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import 'leaflet-routing-machine';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useTranslation } from 'helpers/hooks';

import { exitMap, setSelectedObject, toggleTopTip } from '../../store/map/actions';
import { AppState } from '../../store/interfaces';
import { CloseButton } from '../CloseButton';
import { registerMapCloseTopTipEvent } from '../../services/reactGa';

import { Loading } from './Loading';
import { MapProps } from './interfaces';
import { MapMarkers } from './MapMarkers';
import { PinDetails } from './PinDetails';
import { DriverMarker } from './DriverMarker';
import { RidesMarkers } from './RidesMarkers';

const position = {
  lat: 40.705287916912724,
  lng: -74.01399014489684,
};

// url = 'http://osrm.skeddy.net/osrm/tile/v1/car/tile({x},{y},{z}).mvt';

const TapHint = styled.div`
  text-align: center;
  font-size: 18px;
  padding: 10px 0;
  background: ${(props) => props.theme.background};
  position: relative;
  width: 100%;
`;

const MapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const MapContainerStyled = styled(MapContainer)`
  width: 100%;
  flex: 1;
`;

export const Map: React.FC<MapProps> = React.memo(function ({
  rides,
  isEditMode = false,
  onDropRide,
  hideHint = false,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showTopTip = useSelector((app: AppState) => app.map.showTopTip);
  const handleCloseTopHint = useCallback(() => {
    dispatch(toggleTopTip());
    registerMapCloseTopTipEvent();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(exitMap());
    };
  });

  useEffect(() => {
    if (rides && rides.length === 1) {
      dispatch(setSelectedObject(rides[0]));
    }
  }, [rides]);

  return (
    <>
      <Loading />
      <MapWrapper>
        {isEditMode && showTopTip && !hideHint && (
          <TapHint>
            {t('map.tapAnywhere')}
            <CloseButton size="small" top={8} right={15} onClick={handleCloseTopHint} />
          </TapHint>
        )}
        <MapContainerStyled zoomControl={false} center={position} zoom={13} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {isEditMode ? <MapMarkers /> : <RidesMarkers rides={rides} />}
          <DriverMarker isEditMode={isEditMode} />
          <ZoomControl position="bottomright" />
        </MapContainerStyled>
        <PinDetails rides={rides} isEditMode={isEditMode} />
      </MapWrapper>
    </>
  );
});
